import { useState } from 'react';
import axios from 'axios';
import { Gauge, Loader2, X, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only. Every API call slower than 2 s (path, ms, status, host, env)
// rendered as plain text so the admin can copy/paste it to the developer.
export default function AdminSlowRequestsButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  if (!user || user.role !== 'admin') return null;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/slow-requests?limit=200`, { withCredentials: true });
      const lines = [
        `DiviDrip slow requests (>= ${data.threshold_ms} ms) · generated ${data.generated_at} · host ${data.host}`,
        `page: ${window.location.href}`,
        `browser: ${navigator.userAgent}`,
        `swr snapshot rows: ${data.swr_cache_rows}`,
        '',
        '== BY ENDPOINT (worst first) ==',
        'count  p50_ms  max_ms  path',
        ...data.summary.map((s) => `${String(s.count).padStart(5)}  ${String(s.p50_ms).padStart(6)}  ${String(s.max_ms).padStart(6)}  ${s.path}`),
        '',
        '== RECENT (newest first) ==',
        'timestamp                 ms     status swr        env         path?query',
        ...data.recent.map((r) => `${r.ts.slice(0, 19)}  ${String(r.duration_ms).padStart(6)}  ${r.status}    ${(r.swr || '-').padEnd(9)}  ${(r.env || '-').padEnd(10)}  ${r.method} ${r.path}${r.query ? '?' + r.query : ''}`),
      ];
      setText(lines.join('\n'));
    } catch (e) {
      setText(`Failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); } catch { toast.error('Copy failed — select the text and copy manually'); }
  };
  const clear = async () => {
    await axios.delete(`${API}/admin/slow-requests`, { withCredentials: true });
    toast.success('Slow-request log cleared');
    load();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); load(); }}
        className="inline-flex items-center gap-1.5 rounded-md border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
        title="Admin: every API call over 2 seconds, formatted for copy/paste debugging"
        data-testid="admin-slow-requests-button"
      >
        <Gauge className="w-3.5 h-3.5" /> Slow Requests
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3" onClick={() => setOpen(false)} data-testid="admin-slow-requests-modal">
          <div className="w-full max-w-4xl max-h-[85vh] rounded-xl border bg-card shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
              <h3 className="text-sm font-bold flex items-center gap-2"><Gauge className="w-4 h-4 text-amber-600" /> Slow Requests (&gt; 2 s) — copy &amp; paste to the developer</h3>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold hover:bg-muted" data-testid="admin-slow-requests-copy"><Copy className="w-3.5 h-3.5" /> Copy</button>
                <button type="button" onClick={clear} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold hover:bg-muted" data-testid="admin-slow-requests-clear"><Trash2 className="w-3.5 h-3.5" /> Clear</button>
                <button type="button" onClick={() => setOpen(false)} className="p-1 rounded-md hover:bg-muted" aria-label="Close" data-testid="admin-slow-requests-close"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-3 overflow-auto">
              {loading ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                : <textarea readOnly value={text} className="w-full h-[60vh] font-mono text-[11px] leading-snug bg-muted/40 rounded-md p-2 border resize-none" data-testid="admin-slow-requests-text" />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
